import {Component, OnInit, OnDestroy, ViewChild}    from '@angular/core';
import {NgbActiveModal}                             from "@ng-bootstrap/ng-bootstrap";
import {FormBuilder, FormGroup, Validators}         from "@angular/forms";

import {AngularQuery}                               from "../../services/query/angularquery";
import {ItemComponent}                              from "../../articles/item/item.component";
import {Message}                                    from "../../services/message/message";
import {MessageEntry}                               from "../../services/message/message-entry";
import {Position}                                   from "../../services/position/position";
import {PositionService}                            from "../../services/position/position.service";
import {Sport}                                      from "../../services/sport/sport";
import {SportService}                               from "../../services/sport/sport.service";

import {Subscription}                               from "rxjs/Subscription";
import {isNullOrUndefined}                          from "util";



@Component({
	selector: 'app-position-admin',
	templateUrl: './position-admin.component.html',
	styleUrls: ['./position-admin.component.css']
})



/**
 * Create, edit and remove the Position entries.
 */
export class PositionAdminComponent implements OnInit, OnDestroy {


	hiddenPositions: string[] = ['LWR', 'RWR', 'ORT', 'ORG', 'OLG', 'OLT', 'LDE', 'LDT', 'RDT', 'RDE', 'SSL', 'ML', 'LOLB', 'WSL', 'LILB', 'RILB', 'ROLB', 'LCB', 'RCB'];
	message:Message;
	position: Position[] = [];
	positionFound: Subscription;
	sport: Sport[] = [];
	sportFound: Subscription;
	tForm: FormGroup;


	/**
	 * Initialize the component.
	 *
	 * @param {NgbActiveModal} activeModal       Used to close the page.
	 * @param {FormBuilder} fb                   Used to create the page objects.
	 * @param {PositionService} positionService  Used when handling Position objects.
	 * @param {SportService} sportService        The service that provides Sport information.
	 */
	constructor(
		public activeModal: NgbActiveModal,
		private fb: FormBuilder,
		private positionService: PositionService,
		private sportService: SportService,
	) {}


	/**
	 * Unsubscribe from Subscriptions to avoid memory leaks.
	 */
	ngOnDestroy() {

		if (this.positionFound) {

			this.positionFound.unsubscribe();

		}

		if (this.sportFound) {

			this.sportFound.unsubscribe();

		}

	}


	/**
	 * Initialize the form and load the Sport and Position information.
	 */
	ngOnInit() {

		// Initialize the data structure for the page.
		this.tForm = this.fb.group({
			id: [-1, [Validators.required, Validators.min(-1)]],
			priority: [0, [Validators.required, Validators.min(-100)]],
			title: ['', [Validators.required,Validators.minLength(1), Validators.maxLength(64),]],
			poscode: ['', [Validators.required,Validators.minLength(1), Validators.maxLength(6),]],
			code: ['NFL', [Validators.minLength(3), Validators.maxLength(3)]],
			nfl_inactives_list: [0, [Validators.min(0), Validators.max(1)]],
		});

		// Set up the message window with an initial message.
		let me:MessageEntry = new MessageEntry();
		me.message = 'Loading...';
		me.level = 1;
		this.message = new Message(me);
		this.message.show();

		// Start the retrieval of data; return the Observer and process when data arrives.
		this.sportFound = this.sportService.getSports().subscribe(

			(response) => {

				// Set the array of Sport objects.
				this.sport = response;
				let code:string = this.tForm.controls['code'].value;
				let sport_id:number = this.getSportId();

				if (-1 === sport_id) {

					me.message = "Unable to find the sport entry for '" + code + "'";
					me.level = 4;
					this.message.add(me, true);
					this.message.show();

				} else {

					this.onSportChange(code);

				}

			},

			() => {

				me.message = 'Sport data did not load properly';
				me.level = 4;
				this.message.add(me, true);
				this.message.show();

			}

		);

	}


	/**
	 * Lets me talk to the position SELECT control.
	 */
	@ViewChild('positionSelect')
	positionSelect: any;



	/**
	 * Don't allow the user to close the window if they have unsaved changes.
	 */
	close():void {

		if (! this.tForm.pristine) {

			if (false === confirm("There are unsaved changes. Click 'Cancel' and save your work or 'OK' to abandon the changes")) {

				return;

			}

		}

		this.activeModal.close('Close method');

	}


	/**
	 * Delete the currently selected Position object.
	 */
	deletePosition():void {

		// Notify the user.
		let me:MessageEntry = new MessageEntry();
		let title:string = this.tForm.controls['title'].value;
		me.message = "Deleting '" + title + "' ...";
		me.level = 1;
		this.message.add(me, true);
		this.message.show();

		// Cancel any existing subscriptions.
		if (this.positionFound) {

			this.positionFound.unsubscribe();

		}

		// Try to delete the position and report.
		this.positionFound = this.positionService.deletePosition(this.tForm.controls['id'].value).subscribe(

			() => {

				// Remove the deleted entry.
				for (let entry in this.position) {

					if (this.tForm.controls['id'].value === this.position[entry].id) {

						let index:number = parseInt(entry);
						this.position.splice(index, 1);
						this.onSelectChange(0, true); // Force the SELECT control to the 'Add New' position.
						break;

					}

				}

				me.message = "Deleted '" + title + "'";
				me.level = 2;
				this.message.add(me);
				this.message.show();

			},

			() => {

				me.message = "Unable to delete '" + title + "'";
				me.level = 2;
				this.message.add(me);
				this.message.show();

			}

		);

	}


	/**
	 * Get the Position that matches the provided ID number.
	 *
	 * @returns {Position} A Position object or null if something goes wrong.
	 */
	private getPosition(id:number):Position {

		// This is the 'Add New Position' choice, which isn't in the Position list.
		if (-1 === id) {

			let code:string = this.tForm.controls['code'].value;
			let addnew:Position = new Position();
			addnew.title = '';
			addnew.poscode = '';
			addnew.id = -1;
			addnew.priority = 0;
			addnew.code = code;
			addnew.nfl_inactives_list = 0;
			return addnew;

		}

		for (let entry of this.position) {

			if (id === entry.id) {

				return entry;

			}

		}

		return null;

	}


	/**
	 * Get the ID number of the currently selected sport in the radio buttons.
	 *
	 * @returns {number} -1 if something goes wrong; the ID otherwise.
	 */
	private getSportId():number {

		// Get the ID of the selected entry.
		let sport_id:number = -1;
		let code:string = this.tForm.controls['code'].value;
		for (let entry of this.sport) {

			if (code === entry.db_enum) {

				sport_id = entry.id;
				break;

			}

		}

		return sport_id;

	}


	/**
	 * This is used by the Sport radio button to
	 * turn the selected value a different color.
	 *
	 * @param name  The 'code' value for the radio button.
	 * @returns {any} A style object with content or empty.
	 */
	isHighlighted(name: string): any {

		if (name === this.tForm.controls['code'].value) {

			return {'color': 'blue', 'font-weight': 'bold'};

		}

		return {};

	}


	/**
	 * Triggered whenever there is a change in the Position SELECT
	 * control.
	 *
	 * @param {number} index   The index in the position list that is the new selection.
	 * @param {boolean} force  If true, ignore the check to see if the field is pristine.
	 */
	onSelectChange(index:number, force:boolean = false):void {

		if (! this.tForm.pristine && ! force) {

			if (false === confirm("There are unsaved changes. Click 'Cancel' and save your work or 'OK' to abandon the changes")) {

				return;

			}

		}

		// Set the SELECT to the correct option.
		this.positionSelect.nativeElement.selectedIndex = index;
		let id:number = parseInt(this.positionSelect.nativeElement.options[this.positionSelect.nativeElement.selectedIndex].value);
		let me:MessageEntry = new MessageEntry();

		// Find the data about the Position in the Position list.
		let choice:Position = this.getPosition(id);

		if (isNullOrUndefined(choice)) {

			me.message = 'Unable to find Position to edit';
			me.level = 4;
			this.message.add(me, true);
			this.message.show();
			return;

		}

		// Fill in the editing controls with the data.
		this.tForm.controls['title'].setValue(choice.title);
		this.tForm.controls['id'].setValue(choice.id);
		this.tForm.controls['poscode'].setValue(choice.poscode);
		this.tForm.controls['priority'].setValue(choice.priority);
		this.tForm.controls['nfl_inactives_list'].setValue(choice.nfl_inactives_list);

		// Mark the page as untouched.
		this.tForm.markAsPristine();

		if (-1 === choice.id) {

			me.message = "Ready";

		} else {

			me.message = "'" + choice.title + "' position loaded for editing";

		}

		me.level = 2;
		this.message.add(me);
		this.message.show();

	}


	/**
	 * Manage the settings changes needed when a new
	 * Sport is selected from the radio buttons.
	 *
	 * @param {string} code  The code entry for the Sport ('NFL', 'MLB', etc.) from the ReST API.
	 */
	onSportChange(code:string):void {

		if (! this.tForm.pristine) {

			if (false === confirm("There are unsaved changes. Click 'Cancel' and save your work or 'OK' to abandon the changes")) {

				return;

			} else {

				this.tForm.markAsPristine();

			}

		}

		// Clear the existing position entries.
		this.position.length = 0;
		let me:MessageEntry = new MessageEntry();

		// Cancel any existing subscriptions.
		if (this.positionFound) {

			this.positionFound.unsubscribe();

		}

		// Retrieve the positions, filtering as needed.
		this.positionFound = this.positionService.getPositionsForSelect(code).subscribe(

			(positions) => {

				if (Array.isArray(positions)) {

					// Fill in the positions for this Sport.
					for (let pos of positions) {
						let hidden:boolean = false;

						if ('ALL' === pos.code.toUpperCase()) {

							continue;

						}

						for (let poscode of this.hiddenPositions) {
							if (poscode === pos.poscode) {
								hidden = true;
								break;
							}
						}
						!hidden && this.position.push(new Position(pos));
						
					}

					me.message = this.position.length + ' existing positions loaded';
					me.level = 2;
					this.message.add(me, true);
					this.message.show();

					// Set the SELECT control to the 'Add New' position.
					this.onSelectChange(0, true);

					// Enable or disable the NFL inactives, depending on the sport chosen.
					if ('NFL' === code) {

						this.tForm.get('nfl_inactives_list').enable();

					} else {

						this.tForm.get('nfl_inactives_list').disable();

					}

					return;

				}

				me.message = 'Existing positions did not load properly';
				me.level = 4;
				this.message.add(me, true);
				this.message.show();

			},

			() => {

				me.message = 'Retrieval of existing positions failed';
				me.level = 4;
				this.message.add(me, true);
				this.message.show();

			}

		);

	}


	/**
	 * Save the Position from the field data and add or update
	 * the list of Positions for this sport.
	 *
	 */
	savePosition():void {

		// Notify the user.
		let me:MessageEntry = new MessageEntry();
		let title:string = this.tForm.controls['title'].value;
		title = ItemComponent.replaceWordCharacters(title);
		me.message = "Saving '" + title + "' ...";
		me.level = 1;
		this.message.add(me, true);
		this.message.show();

		// Cancel any existing subscriptions.
		if (this.positionFound) {

			this.positionFound.unsubscribe();

		}

		// Build the query.
		let id:string = (-1 === this.tForm.controls['id'].value) ? 'new' : this.tForm.controls['id'].value.toString();
		let query:AngularQuery = new AngularQuery();
		query.useCacheBuster(false);
		query.useParams(false);
		query.setEndpoint(AngularQuery.ENDPOINT_POSITION, id);

		// Build the JSON object.
		let post:Object = {};
		post['poscode'] = this.tForm.controls['poscode'].value;
		post['title'] = title;
		post['code'] = this.tForm.controls['code'].value;
		post['priority'] = this.tForm.controls['priority'].value;
		post['nfl_inactives_list'] = 'NFL' === this.tForm.controls['code'].value && this.tForm.controls['nfl_inactives_list'].value ? 1 : 0;

		// Save the position.
		this.positionFound = this.positionService.save(query.getUrl(), JSON.stringify(post)).subscribe(

			(response) => {

				this.updateSelect(response);
				this.onSelectChange(0, true);

				me.message = "Saved '" + title + "'";
				me.level = 2;
				this.message.add(me);
				this.message.show();

			},

			() => {

				me.message = "Unable to save '" + title + "'";
				me.level = 2;
				this.message.add(me);
				this.message.show();

			}

		);

	}


	/**
	 * Insert or update the provided Position into
	 * the list the SELECT control draws from.
	 *
	 * @param {Position} pos  The new or revised Position.
	 */
	updateSelect(pos:Position):void {

		for (let entry in this.position) {

			if (pos.id === this.position[entry].id) {

				let index:number = parseInt(entry);
				this.position.splice(index, 1, pos);
				return;

			}

		}

		// Handle new Position entries.
		this.position.push(pos);

	}


}



// WEBPACK FOOTER //
// ./src/app/admin/position/position-admin.component.ts